import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsStreaming, selectMessages } from '../../store/selectors';
import { aiAssistantActions } from '../../store/aiAssistantSlice';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { AssistantSuggestionIcon } from './AssistantSuggestionIcon';

const EMPTY_SUGGESTIONS = [
  'Show any inconsistencies / conflicting information between this RSDs and linked documents?',
  'What are all the updates that have been made to this RSD over the last 7 days?',
  'What critical milestones that are more than 3 days past due their approval date?'
];

interface ChatMessageListProps {
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** embedded = slideout section hero; overlay = timeline dock (same content, wider panel) */
  emptyLayout?: 'embedded' | 'overlay';
}

export function ChatMessageList({
  scrollContainerRef,
  emptyLayout = 'embedded'
}: ChatMessageListProps) {
  const messages = useSelector(selectMessages);
  const isStreaming = useSelector(selectIsStreaming);
  const dispatch = useDispatch();
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  // True while our code is setting scrollTop so the scroll listener ignores it.
  // Browsers fire the scroll event synchronously on scrollTop assignment, which
  // would otherwise reset userScrolledRef back to false after every auto-scroll.
  const programmaticScrollRef = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const hasMessages = messages.length > 0;

  const getScrollParent = useCallback(
    (): HTMLElement | null =>
      scrollContainerRef?.current ??
      (messagesRef.current?.closest('.rau-chat-area') as HTMLElement | null),
    [scrollContainerRef]
  );

  // Detect when the user manually scrolls away from the bottom
  useEffect(() => {
    if (!hasMessages) return;
    const scrollParent = getScrollParent();
    if (!scrollParent) return;
    const onScroll = () => {
      if (programmaticScrollRef.current) return; // ignore our own scrolls
      const atBottom =
        scrollParent.scrollHeight -
          scrollParent.scrollTop -
          scrollParent.clientHeight <
        80;
      userScrolledRef.current = !atBottom;
      setShowScrollButton(!atBottom);
    };
    scrollParent.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', onScroll);
  }, [hasMessages, getScrollParent]);

  // Auto-scroll: one rAF per render cycle, instant to avoid animation stutter.
  // Uses programmaticScrollRef so setting scrollTop doesn't reset the user-scroll flag.
  useEffect(() => {
    if (userScrolledRef.current) return;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (userScrolledRef.current) return;
      const scrollParent = getScrollParent();
      if (!scrollParent) return;
      programmaticScrollRef.current = true;
      scrollParent.scrollTop = scrollParent.scrollHeight;
      programmaticScrollRef.current = false;
    });
  }, [messages, isStreaming, getScrollParent]);

  // Reset scroll-lock when stream ends
  useEffect(() => {
    if (!isStreaming) {
      userScrolledRef.current = false;
      setShowScrollButton(false);
    }
  }, [isStreaming]);

  // Cancel any pending rAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    userScrolledRef.current = false;
    setShowScrollButton(false);
    const scrollParent = getScrollParent();
    if (!scrollParent) return;
    programmaticScrollRef.current = true;
    scrollParent.scrollTo({
      top: scrollParent.scrollHeight,
      behavior: 'smooth'
    });
    // smooth scroll dispatches multiple scroll events; keep the flag set until settled
    setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 300);
  }, [getScrollParent]);

  if (!hasMessages) {
    return (
      <div
        className={[
          'rau-empty-hero',
          emptyLayout === 'overlay' ? 'rau-empty-hero--overlay' : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <h1 className="rau-empty-hero-title">What are you looking for?</h1>
        <div className="rau-empty-tab-composer">
          <ChatInput />
        </div>
        <div className="rau-empty-suggestions" role="list">
          {EMPTY_SUGGESTIONS.map(s => (
            <button
              key={s}
              type="button"
              className="rau-empty-suggestion-row"
              disabled={isStreaming}
              onClick={() =>
                !isStreaming && dispatch(aiAssistantActions.sendMessage(s))
              }
            >
              <span className="rau-empty-suggestion-icon" aria-hidden="true">
                <AssistantSuggestionIcon />
              </span>
              <span className="rau-empty-suggestion-label">{s}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rau-messages" ref={messagesRef}>
      {messages.map(msg => (
        <div key={msg.id}>
          <ChatMessage message={msg} />
        </div>
      ))}
      <div ref={bottomRef} />
      {showScrollButton && (
        <button
          type="button"
          className="rau-scroll-to-bottom"
          onClick={scrollToBottom}
          aria-label="Scroll to bottom"
          title="Scroll to bottom"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 11.5L1.5 5h13L8 11.5z" />
          </svg>
        </button>
      )}
    </div>
  );
}
